import type {
	FailedPendingResponse,
	GlobalPCSetCreateDataModel,
	GlobalPCSetModel,
	GlobalPCSetUpdateDataModel,
	HTTPValidationError,
	PricingCampaignCopySettingsRequestModel,
	PricingCampaignCopySettingsResponseModel,
	PricingCampaignCreateDataModel,
	PricingCampaignDuplicateRequest,
	PricingCampaignModel,
	PricingCampaignProductsFilters,
	PricingCampaignReassignRequest,
	PricingCampaignReassignResponseModel,
	PricingCampaignReorderRequestModel,
	PricingCampaignSettingsExportResponseModel,
	PricingCampaignUpdateDataModel,
} from '@/shared/generated'
import type { UseMutationOptions } from '@tanstack/react-query'
import type { AxiosError } from 'axios'

import { useMutation } from '@tanstack/react-query'
import { omitAll } from 'lodash/fp'

import { client } from '@/shared/api/client'

export function useCreatePricingCampaignMutation(
	options?: UseMutationOptions<
		PricingCampaignModel,
		AxiosError<HTTPValidationError | FailedPendingResponse>,
		PricingCampaignCreateDataModel
	>
) {
	return useMutation<
		PricingCampaignModel,
		AxiosError<HTTPValidationError | FailedPendingResponse>,
		PricingCampaignCreateDataModel
	>({
		mutationKey: ['pricing-campaign-create'],
		mutationFn: (newPricingCampaign) =>
			client.pricingCampaign.pricingCampaignCreate(newPricingCampaign),
		...(options || {}),
	})
}
export function useDeletePricingCampaignMutation(
	options?: UseMutationOptions<
		void,
		AxiosError<HTTPValidationError | FailedPendingResponse>,
		string
	>
) {
	return useMutation<
		void,
		AxiosError<HTTPValidationError | FailedPendingResponse>,
		string
	>({
		mutationKey: ['pricing-campaign-delete'],
		mutationFn: (pricingCampaignId) =>
			client.pricingCampaign.pricingCampaignDelete({ pricingCampaignId }),
		...(options || {}),
	})
}
export function useUpdatePricingCampaignMutation(
	options?: UseMutationOptions<
		PricingCampaignModel,
		AxiosError<HTTPValidationError | FailedPendingResponse>,
		PricingCampaignUpdateDataModel & { id: string }
	>
) {
	return useMutation<
		PricingCampaignModel,
		AxiosError<HTTPValidationError | FailedPendingResponse>,
		PricingCampaignUpdateDataModel & { id: string }
	>({
		mutationKey: ['pricing-campaign-update'],
		mutationFn: (pricingCampaign) =>
			client.pricingCampaign.pricingCampaignUpdate(
				{ pricingCampaignId: pricingCampaign.id },
				omitAll(['id'], pricingCampaign)
			),
		...(options || {}),
	})
}

export function useDublicatePricingCampaignsMutation(
	options?: UseMutationOptions<
		void,
		AxiosError<HTTPValidationError | FailedPendingResponse>,
		PricingCampaignDuplicateRequest
	>
) {
	return useMutation<
		void,
		AxiosError<HTTPValidationError | FailedPendingResponse>,
		PricingCampaignDuplicateRequest
	>({
		mutationFn: (body) =>
			client.pricingCampaign.pricingCampaignsDuplicate(body),
		...(options || {}),
	})
}
export function useBulkDeletePricingCampaignsMutation(
	options?: UseMutationOptions<PromiseSettledResult<void>[], Error, string[]>
) {
	return useMutation<PromiseSettledResult<void>[], Error, string[]>({
		mutationKey: ['pricing-campaign-bulk-delete'],
		mutationFn: (payload) =>
			Promise.allSettled(
				payload.map((pricingCampaignId) =>
					client.pricingCampaign.pricingCampaignDelete({ pricingCampaignId })
				)
			),
		...(options || {}),
	})
}
export function useReassignPricingCampaignsMutation(
	options?: UseMutationOptions<
		PricingCampaignReassignResponseModel[],
		AxiosError<HTTPValidationError | FailedPendingResponse>,
		PricingCampaignReassignRequest
	>
) {
	return useMutation<
		PricingCampaignReassignResponseModel[],
		AxiosError<HTTPValidationError | FailedPendingResponse>,
		PricingCampaignReassignRequest
	>({
		mutationFn: (body) => client.pricingCampaign.pricingCampaignsReassign(body),
		...(options || {}),
	})
}
export function useCopyPricingCampaignSettingsMutation(
	options?: UseMutationOptions<
		PricingCampaignCopySettingsResponseModel[],
		AxiosError<HTTPValidationError | FailedPendingResponse>,
		PricingCampaignCopySettingsRequestModel
	>
) {
	return useMutation<
		PricingCampaignCopySettingsResponseModel[],
		AxiosError<HTTPValidationError | FailedPendingResponse>,
		PricingCampaignCopySettingsRequestModel
	>({
		mutationFn: (body) =>
			client.pricingCampaign.pricingCampaignsCopySettings(body),
		...(options || {}),
	})
}
export type PricingCampaignAssignProductsArgs = {
	pricingCampaignId: string
	data: PricingCampaignProductsFilters
}
export function useAssignProductsMutation(
	options?: UseMutationOptions<
		void,
		AxiosError<HTTPValidationError>,
		PricingCampaignAssignProductsArgs
	>
) {
	return useMutation<
		void,
		AxiosError<HTTPValidationError>,
		PricingCampaignAssignProductsArgs
	>({
		mutationKey: ['pricing-campaign-assign-products'],
		mutationFn: ({
			pricingCampaignId,
			data,
		}: PricingCampaignAssignProductsArgs) =>
			client.pricingCampaign.pricingCampaignAssignProducts(
				{ pricingCampaignId },
				data
			),
		...(options || {}),
	})
}
export function usePricingCampaignUnassignProducts(
	options?: UseMutationOptions<
		void,
		AxiosError<HTTPValidationError | FailedPendingResponse>,
		PricingCampaignProductsFilters
	>
) {
	return useMutation<
		void,
		AxiosError<HTTPValidationError | FailedPendingResponse>,
		PricingCampaignProductsFilters
	>({
		mutationFn: (body: PricingCampaignProductsFilters) =>
			client.pricingCampaign.pricingCampaignUnassignProducts(body),
		...(options || {}),
	})
}
export function useReorderPricingCampaignsMutation(
	options?: UseMutationOptions<
		void,
		AxiosError<HTTPValidationError | FailedPendingResponse>,
		PricingCampaignReorderRequestModel
	>
) {
	return useMutation<
		void,
		AxiosError<HTTPValidationError | FailedPendingResponse>,
		PricingCampaignReorderRequestModel
	>({
		mutationKey: ['pricing-campaign-reorder'],
		mutationFn: (payload) =>
			client.pricingCampaign.pricingCampaignReorder(payload),
		...(options || {}),
	})
}
export function useCreateGlobalPcSetMutation(
	options?: UseMutationOptions<
		GlobalPCSetModel,
		AxiosError<HTTPValidationError>,
		GlobalPCSetCreateDataModel
	>
) {
	return useMutation<
		GlobalPCSetModel,
		AxiosError<HTTPValidationError>,
		GlobalPCSetCreateDataModel
	>({
		mutationFn: (payload: GlobalPCSetCreateDataModel) =>
			client.globalPcSet.globalPcSetCreate(payload),
		...(options || {}),
	})
}

export function useDublicateGlobalPcSetMutation(
	options?: UseMutationOptions<
		GlobalPCSetModel,
		AxiosError<HTTPValidationError | FailedPendingResponse>,
		string
	>
) {
	return useMutation<
		GlobalPCSetModel,
		AxiosError<HTTPValidationError | FailedPendingResponse>,
		string
	>({
		mutationFn: (globalPcSetId) =>
			client.globalPcSet.globalPcSetDuplicate({ globalPcSetId }),
		...(options || {}),
	})
}

export function useDeleteGlobalPcSetMutation(
	options?: UseMutationOptions<
		void,
		AxiosError<HTTPValidationError | FailedPendingResponse>,
		string
	>
) {
	return useMutation<
		void,
		AxiosError<HTTPValidationError | FailedPendingResponse>,
		string
	>({
		mutationFn: (globalPcSetId) =>
			client.globalPcSet.globalPcSetDelete({ globalPcSetId }),
		...(options || {}),
	})
}

export function useSetMainGlobalPcSetMutation(
	options?: UseMutationOptions<
		void,
		AxiosError<HTTPValidationError | FailedPendingResponse>,
		string
	>
) {
	return useMutation<
		void,
		AxiosError<HTTPValidationError | FailedPendingResponse>,
		string
	>({
		mutationFn: async (globalPcSetId) => {
			await client.globalPcSet.globalPcSetSetMain({ globalPcSetId })
		},
		...(options || {}),
	})
}

export function useUpdateGlobalPcSetMutation(
	globalPcSetId: string,
	options?: UseMutationOptions<
		void,
		AxiosError<HTTPValidationError | FailedPendingResponse>,
		GlobalPCSetUpdateDataModel
	>
) {
	return useMutation<
		void,
		AxiosError<HTTPValidationError | FailedPendingResponse>,
		GlobalPCSetUpdateDataModel
	>({
		mutationFn: async (payload: GlobalPCSetUpdateDataModel) => {
			await client.globalPcSet.globalPcSetUpdate({ globalPcSetId }, payload)
		},
		...(options || {}),
	})
}

export function useExportPricingCampaignsSettingsMutation(
	options?: UseMutationOptions<
		PricingCampaignSettingsExportResponseModel,
		AxiosError<HTTPValidationError>
	>
) {
	return useMutation<
		PricingCampaignSettingsExportResponseModel,
		AxiosError<HTTPValidationError>
	>({
		mutationFn: () => client.pricingCampaign.pricingCampaignSettingsExport(),
		...(options || {}),
	})
}
